/**
 * Soren Past Answers — Durable Server-Stored History.
 * Replaces client-session storage with server API (/api/soren/answers) and React Query.
 *
 * All answers are owner-scoped on the server.
 * Answers older than 30 days automatically transition to Archive.
 */

import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'
import type { AriaChatReply } from '../../api/ariaChat'

export type PastAnswerType =
  | 'Cash'
  | 'Margin'
  | 'Cost'
  | 'Payments'
  | 'Lien'
  | 'Billing'
  | 'Subs'
  | 'Answer'

export type PastAnswerStatus =
  | 'ready'
  | 'awaiting_confirmation'
  | 'confirmed'
  | 'dismissed'
  | 'expired'

export interface ServerPastAnswer {
  id: string
  user_id: number
  project_id: number | null
  question: string
  question_type: string
  one_line_result: string
  answer_json: Record<string, unknown>
  tool_names: string[]
  status: PastAnswerStatus
  is_archived: boolean
  created_at: string
  archived_at: string | null
}

export interface PastAnswer {
  id: string
  question: string
  type: PastAnswerType
  timeLabel: string
  oneLineResult: string
  status: PastAnswerStatus
  awaitingConfirmation: boolean
  replyData?: AriaChatReply | null
  answerJson: Record<string, unknown>
  toolNames: string[]
  projectId: number | null
  isArchived: boolean
  createdAt: number
  rawCreatedAt: string
}

export function formatTimeLabel(dateInput: Date | string = new Date()): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
  if (isNaN(date.getTime())) return 'Recently'
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  const isToday = new Date().toDateString() === date.toDateString()
  if (isToday) return `Today · ${time}`
  const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${monthDay} · ${time}`
}

export function detectAnswerType(question: string): PastAnswerType {
  const q = question.toLowerCase()
  if (q.includes('cash') || q.includes('runway') || q.includes('balance') || q.includes('forecast')) {
    return 'Cash'
  }
  if (q.includes('margin') || q.includes('profit') || q.includes('markup')) {
    return 'Margin'
  }
  if (q.includes('cost') || q.includes('expense') || q.includes('rental') || q.includes('lift')) {
    return 'Cost'
  }
  if (
    q.includes('pay app') ||
    q.includes('overdue') ||
    q.includes('payment') ||
    q.includes('follow-up') ||
    q.includes('invoice')
  ) {
    return 'Payments'
  }
  if (q.includes('lien') || q.includes('pre lien') || q.includes('waiver')) {
    return 'Lien'
  }
  if (q.includes('bill') || q.includes('sov') || q.includes('left to bill')) {
    return 'Billing'
  }
  if (q.includes('sub') || q.includes('vendor') || q.includes('commitment')) {
    return 'Subs'
  }
  return 'Answer'
}

export function mapServerToPastAnswer(row: ServerPastAnswer): PastAnswer {
  const status = (row.status || 'ready') as PastAnswerStatus
  const awaitingConfirmation = status === 'awaiting_confirmation'
  const createdDate = new Date(row.created_at)
  const timeLabel = formatTimeLabel(createdDate)

  let replyData: AriaChatReply | null = null
  if (row.answer_json?.reply_data) {
    replyData = row.answer_json.reply_data as AriaChatReply
  } else if (row.answer_json?.content) {
    replyData = {
      role: 'assistant',
      content: String(row.answer_json.content),
    }
  }

  return {
    id: row.id,
    question: row.question,
    type: (row.question_type as PastAnswerType) || detectAnswerType(row.question),
    timeLabel,
    oneLineResult: row.one_line_result,
    status,
    awaitingConfirmation,
    replyData,
    answerJson: row.answer_json || {},
    toolNames: Array.isArray(row.tool_names) ? row.tool_names : [],
    projectId: row.project_id,
    isArchived: Boolean(row.is_archived),
    createdAt: createdDate.getTime(),
    rawCreatedAt: row.created_at,
  }
}

export interface AnswersListResponse {
  items: ServerPastAnswer[]
  recentCount: number
  archiveCount: number
  totalCount: number
  hasMore: boolean
  nextCursor: string | null
}

export async function fetchSorenAnswers(params: {
  filter?: 'recent' | 'archive'
  project_id?: number | null
  limit?: number
  cursor?: string | null
  offset?: number
}): Promise<AnswersListResponse> {
  const query = new URLSearchParams()
  if (params.filter) query.set('filter', params.filter)
  if (params.project_id != null) query.set('project_id', String(params.project_id))
  if (params.limit != null) query.set('limit', String(params.limit))
  if (params.cursor) query.set('cursor', params.cursor)
  if (params.offset != null) query.set('offset', String(params.offset))

  const qs = query.toString()
  const res = await apiFetch<{ data: AnswersListResponse }>(
    'GET',
    `/api/soren/answers${qs ? `?${qs}` : ''}`
  )

  return (
    res.data ?? {
      items: [],
      recentCount: 0,
      archiveCount: 0,
      totalCount: 0,
      hasMore: false,
      nextCursor: null,
    }
  )
}

export async function createSorenAnswer(payload: {
  id?: string
  question: string
  question_type?: string
  one_line_result?: string
  answer_json?: Record<string, unknown>
  tool_names?: string[]
  project_id?: number | null
  status?: string
}): Promise<ServerPastAnswer> {
  const res = await apiFetch<{ data: ServerPastAnswer }>('POST', '/api/soren/answers', payload)
  return res.data!
}

export async function updateSorenAnswerStatus(
  id: string,
  status: PastAnswerStatus,
  actionResult?: unknown
): Promise<ServerPastAnswer> {
  const res = await apiFetch<{ data: ServerPastAnswer }>(
    'PATCH',
    `/api/soren/answers/${encodeURIComponent(id)}/status`,
    {
      status,
      action_result: actionResult,
    }
  )
  return res.data!
}

export async function archiveSorenAnswer(id: string): Promise<ServerPastAnswer> {
  const res = await apiFetch<{ data: ServerPastAnswer }>(
    'POST',
    `/api/soren/answers/${encodeURIComponent(id)}/archive`
  )
  return res.data!
}

/**
 * Query hook for past answers with filter (recent vs archive) and pagination.
 */
export function useSorenAnswers(options: {
  filter?: 'recent' | 'archive'
  projectId?: number | null
  pageSize?: number
} = {}) {
  const queryClient = useQueryClient()
  const filter = options.filter || 'recent'
  const projectId = options.projectId ?? null
  const pageSize = options.pageSize || 20

  const [page, setPage] = useState(1)
  const currentLimit = page * pageSize

  const queryKey = ['soren-answers', filter, projectId, currentLimit]

  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchSorenAnswers({
        filter,
        project_id: projectId,
        limit: currentLimit,
      }),
    staleTime: 15_000,
  })

  const rawData = query.data
  const items = (rawData?.items || []).map(mapServerToPastAnswer)
  const recentCount = rawData?.recentCount ?? 0
  const archiveCount = rawData?.archiveCount ?? 0
  const totalCount = rawData?.totalCount ?? 0
  const hasMore = Boolean(rawData?.hasMore)

  const loadMore = useCallback(() => {
    if (hasMore && !query.isFetching) {
      setPage((prev) => prev + 1)
    }
  }, [hasMore, query.isFetching])

  const createMutation = useMutation({
    mutationFn: createSorenAnswer,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['soren-answers'] })
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
      actionResult,
    }: {
      id: string
      status: PastAnswerStatus
      actionResult?: unknown
    }) => updateSorenAnswerStatus(id, status, actionResult),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['soren-answers'] })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: archiveSorenAnswer,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['soren-answers'] })
    },
  })

  return {
    items,
    recentCount,
    archiveCount,
    totalCount,
    hasMore,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    loadMore,
    refetch: query.refetch,
    createAnswer: createMutation.mutateAsync,
    updateStatus: updateStatusMutation.mutateAsync,
    archiveAnswer: archiveMutation.mutateAsync,
  }
}
